import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { resolveImagePreset } from './image-presets'
import { getApiBaseUrl } from './config'
import { buildImageCdnUrl } from './buildImageCdnUrl'
import { extractRelativeMediaPath } from './sync-media-caption'
import { formatSiteDateTime } from '@/lib/site-datetime'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Treat null, empty, and literal "null"/"undefined" as no media path */
export function normalizeMediaPath(path: string | null | undefined): string {
  if (path == null) return ''
  const trimmed = String(path).trim()
  if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
    return ''
  }
  return trimmed
}

/**
 * Raw media file URL (video/mp4, pdf, …).
 * Local-first açıksa /storage; aksi halde assets CDN.
 */
export function getMediaFileUrl(path: string | null | undefined): string {
  const normalized = normalizeMediaPath(path)
  if (!normalized) return ''
  if (normalized.startsWith('data:') || normalized.startsWith('blob:')) {
    return normalized
  }

  // Absolute URL: kendi medyamızsa göreli path’e indir (CDN/LAN host 404 önler)
  if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
    const relative = extractRelativeMediaPath(normalized)
    if (relative) return getMediaFileUrl(relative)
    return normalized
  }

  let cleanPath = normalized.startsWith('/') ? normalized.slice(1) : normalized
  if (cleanPath.startsWith('storage/')) cleanPath = cleanPath.slice(8)
  if (!cleanPath) return ''

  if (prefersLocalMediaStorage()) {
    // Aynı origin /storage → Next rewrite / nginx proxy (API host uyumsuzluğu yok)
    if (typeof window !== 'undefined') {
      return `/storage/${cleanPath}`
    }
    return `${String(getApiBaseUrl()).replace(/\/$/, '')}/storage/${cleanPath}`
  }

  let base = ''
  if (typeof window !== 'undefined') {
    const w = window as any
    base =
      (w.__mediaAssetsBaseUrl as string) ||
      (typeof localStorage !== 'undefined'
        ? localStorage.getItem('__mediaAssetsBaseUrl') || ''
        : '') ||
      ''
  } else if (typeof process !== 'undefined' && process.env) {
    base =
      process.env.NEXT_PUBLIC_MEDIA_ASSETS_URL ||
      process.env.NEXT_PUBLIC_ASSETS_CDN_URL ||
      ''
  }

  if (base?.trim()) {
    const b = base.replace(/^https?:\/\//, '').replace(/\/$/, '')
    const withScheme = /^https?:\/\//i.test(base.trim())
      ? base.trim().replace(/\/$/, '')
      : `https://${b}`
    return `${withScheme}/${cleanPath}`
  }

  return `${String(getApiBaseUrl()).replace(/\/$/, '')}/storage/${cleanPath}`
}

/** Sadece CDN yokken local /storage. Panel keepLocal upload içindir, gösterimi CDN’den ayırmaz. */
export function prefersLocalMediaStorage(): boolean {
  if (typeof window !== 'undefined') {
    const w = window as any
    if (w.__mediaCdnBaseUrl?.trim()) return false
    try {
      const cdn = localStorage.getItem('__mediaCdnBaseUrl')
      if (cdn?.trim()) return false
      const raw = localStorage.getItem('__mediaSettings')
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed?.providers?.s3?.publicBaseUrl?.trim()) return false
      }
    } catch {
      /* ignore */
    }
  }
  if (process.env.NEXT_PUBLIC_IMAGE_CDN_DOMAIN?.trim()) return false
  return process.env.NEXT_PUBLIC_MEDIA_PREFER_LOCAL === '1'
}

/** SSR/client hydration uyumu — SITE_TIMEZONE (@/lib/site-datetime) */
export function formatDate(date: Date | string) {
  return formatSiteDateTime(date, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Parse preset string to query parameters (legacy fallback)
 */
function parsePresetToQuery(preset?: string): string {
  const options = resolveImagePreset(preset);
  const params = new URLSearchParams();
  
  if (options.width) params.set('width', options.width.toString());
  if (options.height) params.set('height', options.height.toString());
  if (options.quality) params.set('quality', options.quality.toString());
  if (options.format) params.set('format', options.format);
  if (options.fit) params.set('fit', options.fit);
  
  return params.toString();
}

/**
 * Get image URL with CDN and query string optimization
 * This function handles both legacy paths and new media system
 * CDN handles image transformations via query strings (e.g., ?width=400&format=webp)
 * IMPORTANT: This function must return the SAME URL on both server and client to avoid hydration errors
 * The inline script in layout.tsx sets window.__mediaCdnBaseUrl before React hydration,
 * ensuring both server and client use the same URL
 * 
 * @param image - Image path string or MediaAsset object
 * @param size - Preset string (e.g., rw400h300q80gc) or preset key - converted to query params
 * @returns Public URL for the image with query string parameters
 */
export function getImageUrl(image: string | null | undefined | {
  url?: string;
  urlOriginal?: string;
  variants?: Record<string, { url?: string }>;
}, size?: string): string {
  // MediaAsset object — avoid circular import with ./media
  if (typeof image === 'object' && image !== null) {
    if (size && image.variants) {
      const variant =
        image.variants[size] ||
        image.variants[`${size}@2x`] ||
        image.variants.original;
      if (variant?.url) return variant.url;
    }
    return image.urlOriginal || image.url || '';
  }

  const normalized = normalizeMediaPath(image)
  if (!normalized) {
    return ''
  }

  // Handle string path (legacy)
  const imagePath = normalized;
  
  // Absolute URL: kendi medyamızsa göreli path’e indirip CDN’e çöz
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    const relative = extractRelativeMediaPath(imagePath);
    if (relative) {
      return getImageUrl(relative, size);
    }
    return imagePath;
  }

  if (imagePath.startsWith('data:') || imagePath.startsWith('blob:')) {
    return imagePath;
  }
  
  // Remove leading slash if exists
  let cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath
  
  // Remove 'storage/' prefix if exists (some paths might have it)
  cleanPath = cleanPath.startsWith('storage/') ? cleanPath.substring(8) : cleanPath
  
  // Ensure path is not empty after cleaning
  if (!cleanPath || cleanPath.trim() === '') {
    return ''
  }
  
  // Normalize path once (remove leading slashes)
  const normalizedPath = cleanPath.replace(/^\/+/, '');

  // Local-first: CDN’de dosya yok — API /storage kullan
  if (prefersLocalMediaStorage()) {
    const options = resolveImagePreset(size);
    const needsTransform =
      size &&
      (options.width !== undefined ||
        options.height !== undefined ||
        (options.format && !['jpeg', 'jpg', 'png'].includes(options.format)));

    if (needsTransform) {
      return buildImageCdnUrl(normalizedPath, options, false, undefined, {
        apiBaseUrl: getApiBaseUrl(),
      });
    }

    if (typeof window !== 'undefined') {
      return `/storage/${normalizedPath}`;
    }
    const storageBase = String(getApiBaseUrl()).replace(/\/$/, '');
    return `${storageBase}/storage/${normalizedPath}`;
  }
  
  // Build base URL
  let baseUrl = '';
  
  // CLIENT-SIDE: Always use S3 CDN URL (set by inline script in layout)
  if (typeof window !== 'undefined') {
    // Priority 1: Check window CDN URL cache (fastest, set by inline script)
    const cachedCdnUrl = (window as any).__mediaCdnBaseUrl;
    if (cachedCdnUrl && cachedCdnUrl.trim() !== '' && !cachedCdnUrl.includes('localhost')) {
      baseUrl = cachedCdnUrl;
    } else {
      // Priority 2: Check localStorage for CDN URL
      try {
        const storedCdnUrl = localStorage.getItem('__mediaCdnBaseUrl');
        if (storedCdnUrl && storedCdnUrl.trim() !== '' && !storedCdnUrl.includes('localhost')) {
          (window as any).__mediaCdnBaseUrl = storedCdnUrl;
          baseUrl = storedCdnUrl;
        }
      } catch (e) {
        // localStorage error, continue
      }
      
      // Priority 3: Trigger async load if not attempted yet (non-blocking)
      if (!baseUrl && !(window as any).__mediaSettingsLoadAttempted) {
        (window as any).__mediaSettingsLoadAttempted = true;
        import('./media-url-helper').then(({ preloadMediaSettings }) => {
          preloadMediaSettings().then(() => {
            window.dispatchEvent(new CustomEvent('mediaSettingsLoaded'));
          });
        });
      }
    }
  } else if (typeof process !== 'undefined' && process.env) {
    // SERVER / RSS / SSR: env CDN (window yok) — transform API’ye düşmesin
    baseUrl =
      process.env.NEXT_PUBLIC_IMAGE_CDN_DOMAIN?.trim() ||
      process.env.NEXT_PUBLIC_IMAGE_CDN_URL?.trim() ||
      process.env.NEXT_PUBLIC_MEDIA_ASSETS_URL?.trim() ||
      process.env.NEXT_PUBLIC_ASSETS_CDN_URL?.trim() ||
      '';
  }
  
  // If no CDN URL found, use local storage + transform API (development)
  if (!baseUrl || baseUrl.trim() === '') {
    const options = resolveImagePreset(size);
    const needsTransform =
      size &&
      (options.width !== undefined ||
        options.height !== undefined ||
        (options.format && !['jpeg', 'jpg', 'png'].includes(options.format)));

    if (needsTransform) {
      return buildImageCdnUrl(normalizedPath, options, false, undefined, {
        apiBaseUrl: getApiBaseUrl(),
      });
    }

    if (typeof window !== 'undefined') {
      return `/storage/${normalizedPath}`;
    }
    const storageBase = String(getApiBaseUrl()).replace(/\/$/, '');
    return `${storageBase}/storage/${normalizedPath}`;
  }
  
  // CDN domain present → CloudFront/DIT URLs only (never Nest transform)
  try {
    const options = resolveImagePreset(size);
    const domain = baseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
    return buildImageCdnUrl(normalizedPath, options, domain);
  } catch (error) {
    const queryString = parsePresetToQuery(size);
    if (queryString && queryString.trim() !== '') {
      return `${baseUrl}/${normalizedPath}?${queryString}`;
    }
    return `${baseUrl}/${normalizedPath}`;
  }
}


/**
 * Generate slug from Turkish text
 * Converts Turkish characters to ASCII equivalents
 */
export function generateSlug(text: string): string {
  if (!text || !text.trim()) return ''
  
  const turkishChars: { [key: string]: string } = {
    'ç': 'c', 'Ç': 'C',
    'ğ': 'g', 'Ğ': 'G',
    'ı': 'i', 'İ': 'I',
    'ö': 'o', 'Ö': 'O',
    'ş': 's', 'Ş': 'S',
    'ü': 'u', 'Ü': 'U',
  }
  
  return text
    .split('')
    .map(char => turkishChars[char] || char)
    .join('')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special chars
    .replace(/[\s_-]+/g, '-') // Replace spaces, underscores, multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
}


/**
 * Get final slug - uses provided slug or generates from title
 * Single helper function to ensure consistency across all forms
 * This prevents duplicate slug logic throughout the codebase
 */
export function getFinalSlug(slug: string | undefined | null, title: string): string {
  if (slug && slug.trim()) {
    return slug.trim()
  }
  return generateSlug(title)
}
